import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { scanQrCode } from '../services/auth';
import { formatCurrency } from '../utils/formatters';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import '../styles/QRScanner.css';

const QRScanner = ({ userId, onRegistration, onScan, scanType }) => {
  const [scanning, setScanning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [scanner, setScanner] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [manualCode, setManualCode] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const scannerRef = useRef(null);
  
  // Set title based on scan type
  const getTitle = () => {
    // Jacket scan type removed - stretch goal for future parties
    if (userId) return 'Scan Bonus QR Code';
    return 'Register Your Account';
  };
  
  // Initialize scanner
  useEffect(() => {
    if (scanning && !scanner) {
      try {
        const html5QrCode = new Html5Qrcode("qr-reader");
        setScanner(html5QrCode);
        
        const qrConfig = {
          fps: 10,
          qrbox: 250,
          aspectRatio: 1.0,
          disableFlip: false,
        };
        
        html5QrCode.start(
          { facingMode: "environment" },
          qrConfig,
          onScanSuccess,
          onScanFailure
        )
        .catch(err => {
          console.error("Error starting scanner:", err);
          setErrorMessage(`Camera error: ${err.message || 'Could not access camera'}`);
          setScanning(false);
          setScanner(null);
        });
      } catch (error) {
        console.error("Scanner initialization error:", error);
        setErrorMessage(`Camera initialization failed: ${error.message}`);
        setScanning(false);
      }
    }
    
    return () => {
      if (scanner) {
        try {
          scanner.stop()
            .then(() => {
              console.log("Scanner stopped");
            })
            .catch(err => {
              console.error("Error stopping scanner:", err);
            })
            .finally(() => {
              setScanner(null);
            });
        } catch (error) {
          console.error("Error in cleanup:", error);
          setScanner(null);
        }
      }
    };
  }, [scanning, scanner]);
  
  // Process a code (from scanner or manual entry)
  const processCode = async (code) => {
    console.log("Processing code:", code);
    
    try {
      // Use the onScan prop if provided (for direct handling by parent)
      if (onScan) {
        await onScan(code);
        setScanning(false);
        return;
      }
      
      // Jacket QR code handling removed - stretch goal for future parties

      // Handle registration QR code
      if (!userId && code.startsWith('REG-')) {
        const result = await scanQrCode(null, code, 'REGISTRATION');
        setSuccessMessage('Registration successful!');
        setLastResult({
          type: 'REGISTRATION',
          userId: result.userId,
          message: 'Account registered successfully'
        });
        
        // Call onRegistration callback
        if (onRegistration) {
          onRegistration(result.userId, result.token);
        }
      } 
      // Handle bonus QR code
      else if (userId && code.startsWith('BONUS-')) {
        const result = await scanQrCode(userId, code, 'BONUS');
        const bonusAmount = result.bonusValue;
        
        setLastResult({
          type: 'BONUS',
          value: bonusAmount,
          message: `${bonusAmount >= 0 ? 'Bonus' : 'Penalty'} applied: ${formatCurrency(bonusAmount)}`
        });
        
        setSuccessMessage(
          bonusAmount >= 0 
            ? `Bonus received: ${formatCurrency(bonusAmount)}!` 
            : `Penalty applied: ${formatCurrency(bonusAmount)}`
        );
      } else {
        throw new Error('Invalid code format');
      }
    } catch (error) {
      console.error("Code processing error:", error);
      setErrorMessage(`Error: ${error.message || 'Failed to process code'}`);
    }
  };
  
  // linkJacket function removed - stretch goal for future parties

  // QR code scan success handler
  const onScanSuccess = async (decodedText) => {
    // Pause scanner temporarily
    if (scanner) {
      try {
        await scanner.pause();
      } catch (error) {
        console.error("Error pausing scanner:", error);
      }
    }
    
    console.log("QR Code detected:", decodedText);
    
    // Process the code
    await processCode(decodedText);
    
    // Stop scanning
    setScanning(false);
  };
  
  // QR scan failure handler
  const onScanFailure = (error) => {
    // Ignore errors during scanning, they're usually just frames without QR codes
    console.debug("No QR code found in this frame");
  };
  
  // Start/stop scanning
  const toggleScanner = () => {
    if (scanning) {
      if (scanner) {
        try {
          scanner.stop()
            .then(() => {
              console.log("Scanner stopped");
              setScanner(null);
              setScanning(false);
            })
            .catch(err => {
              console.error("Error stopping scanner:", err);
              // Even if there's an error, update the UI state
              setScanner(null);
              setScanning(false);
            });
        } catch (err) {
          console.error("Error stopping scanner:", err);
          setScanner(null);
          setScanning(false);
        }
      } else {
        setScanning(false);
      }
    } else {
      setScanning(true);
      setErrorMessage('');
      setSuccessMessage('');
    }
  };
  
  // Toggle between scanner and manual entry
  const toggleEntryMethod = () => {
    // If currently scanning, stop the scanner
    if (scanning) {
      toggleScanner();
    }
    
    setShowManualEntry(!showManualEntry);
    setManualCode('');
  };
  
  // Handle manual code submission
  const handleManualSubmit = async (e) => {
    e.preventDefault();
    
    if (!manualCode.trim()) {
      setErrorMessage('Please enter a code');
      return;
    }
    
    // Process the manually entered code
    await processCode(manualCode.trim());
    setManualCode('');
  };
  
  // Clear result and messages
  const resetScan = () => {
    setLastResult(null);
    setErrorMessage('');
    setSuccessMessage('');
    setManualCode('');
    setShowManualEntry(false);
  };
  
  // Get the appropriate placeholder text for manual entry
  const getPlaceholderText = () => {
    // Jacket scan type removed - stretch goal for future parties
    if (userId) return "Enter bonus code (e.g., BONUS-123abc)";
    return "Enter registration code (e.g., REG-123abc)";
  };

  // Get help text for manual entry
  const getHelpText = () => {
    // Jacket help text removed - stretch goal for future parties
    if (userId) return "Bonus codes start with 'BONUS-' and are case-sensitive.";
    return "Registration codes start with 'REG-' and are case-sensitive.";
  };
  
  return (
    <div className="qr-scanner-container">
      <h2>{getTitle()}</h2>
      
      {!scanning && !lastResult && !errorMessage && !successMessage && !showManualEntry && (
        <div className="scanner-instructions">
          <p>
            {userId
              ? 'Scan QR codes to receive bonuses from game wins'
              : 'Scan your registration QR code to create your account'
            }
          </p>
        </div>
      )}
      
      {/* Error message */}
      {errorMessage && (
        <div className="scanner-error">
          <p>{errorMessage}</p>
          <button className="retry-button" onClick={resetScan}>
            Try Again
          </button>
        </div>
      )}
      
      {/* Success message */}
      {successMessage && (
        <div className="scanner-success">
          <p>{successMessage}</p>
          <button className="continue-button" onClick={resetScan}>
            Continue
          </button>
        </div>
      )}
      
      {/* Last scan result */}
      {lastResult && (
        <div className="scan-result">
          <h3>
            {lastResult.type === 'REGISTRATION'
              ? 'Registration Complete'
              : 'Bonus Received'}
          </h3>
          <p>{lastResult.message}</p>
          <button className="continue-button" onClick={resetScan}>
            Continue
          </button>
        </div>
      )}
      
      {/* Entry method toggle */}
      {!lastResult && !errorMessage && !successMessage && (
        <div className="entry-toggle">
          <button 
            className={`toggle-button ${!showManualEntry ? 'active' : ''}`}
            onClick={() => showManualEntry && toggleEntryMethod()}
          >
            Scan with Camera
          </button>
          <button 
            className={`toggle-button ${showManualEntry ? 'active' : ''}`}
            onClick={() => !showManualEntry && toggleEntryMethod()}
          >
            Enter Code Manually
          </button>
        </div>
      )}
      
      {/* Scanner */}
      {!showManualEntry && !lastResult && !errorMessage && !successMessage && (
        <div className="scanner-area">
          {scanning ? (
            <div className="active-scanner">
              <div id="qr-reader" ref={scannerRef}></div>
              <button className="cancel-scan" onClick={toggleScanner}>
                Cancel
              </button>
            </div>
          ) : (
            <button 
              className="start-scan" 
              onClick={toggleScanner}
            >
              {scanType === 'jacket' 
                ? 'Scan Jacket QR Code' 
                : userId 
                  ? 'Scan Bonus QR Code' 
                  : 'Scan Registration QR Code'}
            </button>
          )}
        </div>
      )}
      
      {/* Manual Code Entry */}
      {showManualEntry && !lastResult && !errorMessage && !successMessage && (
        <div className="manual-entry">
          <p className="manual-instructions">Enter your code below:</p>
          <form onSubmit={handleManualSubmit}>
            <input
              type="text"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              placeholder={getPlaceholderText()}
              className="code-input"
            />
            <button type="submit" className="submit-code">
              Submit Code
            </button>
          </form>
          <p className="manual-help-text">{getHelpText()}</p>
        </div>
      )}
    </div>
  );
};

export default QRScanner;